/* eslint-disable */
import React, { useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { toast } from "react-toastify";

import { GET_AI_RECOMMENDATIONS, GET_AI_ACTION_PLAN } from "../../gql/queries/ai";
import { GENERATE_AI_RECOMMENDATIONS, GENERATE_AI_ACTION_PLAN, MARK_RECOMMENDATION_APPLIED } from "../../gql/mutations/ai";

// --------------- Styled Components ---------------

const TabWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  "& .section-title": {
    fontWeight: 600,
    fontSize: "1.25rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const PlaceholderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(8),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
}));

const ActionPlanItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
  "& .rank-badge": {
    minWidth: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.875rem",
  },
}));

const RecommendationCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  "& .fix-code": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflowX: "auto",
    fontFamily: "monospace",
    fontSize: "0.8125rem",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));

// --------------- Helpers ---------------

const priorityColorMap = {
  critical: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const impactColorMap = {
  high: "success",
  medium: "warning",
  low: "default",
};

const difficultyColorMap = {
  easy: "success",
  medium: "warning",
  hard: "error",
};

// --------------- Component ---------------

const AiRecommendationsTab = ({ sessionId, projectId }) => {
  // ---- Mutations ----
  const [generateRecommendations, { loading: genRecLoading }] = useMutation(GENERATE_AI_RECOMMENDATIONS, {
    variables: { sessionId },
    onCompleted: () => {
      toast.success("AI recommendations generated successfully");
      fetchRecommendations({ variables: { sessionId } });
    },
    onError: err => toast.error(err.message),
  });

  const [generateActionPlan, { loading: genPlanLoading }] = useMutation(GENERATE_AI_ACTION_PLAN, {
    variables: { sessionId },
    onCompleted: () => {
      toast.success("Action plan generated successfully");
      fetchActionPlan({ variables: { sessionId } });
    },
    onError: err => toast.error(err.message),
  });

  const [markApplied, { loading: markLoading }] = useMutation(MARK_RECOMMENDATION_APPLIED, {
    onCompleted: () => {
      toast.success("Recommendation marked as applied");
      fetchRecommendations({ variables: { sessionId } });
    },
    onError: err => toast.error(err.message),
  });

  // ---- Queries ----
  const [fetchRecommendations, { data: recData, loading: recLoading }] = useLazyQuery(GET_AI_RECOMMENDATIONS, { fetchPolicy: "network-only" });

  const [fetchActionPlan, { data: planData, loading: planLoading }] = useLazyQuery(GET_AI_ACTION_PLAN, { fetchPolicy: "network-only" });

  // ---- Handlers ----
  const handleGenerate = useCallback(async () => {
    await generateActionPlan();
    await generateRecommendations();
  }, [generateActionPlan, generateRecommendations]);

  const handleMarkApplied = useCallback(
    id => {
      markApplied({ variables: { id } });
    },
    [markApplied],
  );

  // ---- Derived state ----
  const recommendations = recData?.getAiRecommendations || [];
  const actionPlan = planData?.getActionPlan || null;
  const isGenerating = genRecLoading || genPlanLoading;
  const isLoading = recLoading || planLoading;
  const hasData = recommendations.length > 0 || actionPlan;

  // ---- Parse action plan items (stored as JSON string) ----
  let actionPlanItems = [];
  if (actionPlan?.items) {
    try {
      actionPlanItems = typeof actionPlan.items === "string" ? JSON.parse(actionPlan.items) : actionPlan.items;
    } catch {
      actionPlanItems = [];
    }
  }

  return (
    <TabWrapper>
      {/* Generate Button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGenerate}
          disabled={isGenerating || !sessionId}
          size="large"
        >
          {isGenerating ? "Generating Analysis..." : "Generate AI Analysis"}
        </Button>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Placeholder when no data */}
      {!isLoading && !hasData && (
        <PlaceholderBox>
          <LightbulbIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom>
            No AI Analysis Yet
          </Typography>
          <Typography variant="body2">Click "Generate AI Analysis" to analyze issues with AI and get actionable fix recommendations.</Typography>
        </PlaceholderBox>
      )}

      {/* Action Plan Section */}
      {actionPlan && (
        <Box sx={{ mb: 4 }}>
          <Typography className="section-title">
            <PlaylistAddCheckIcon color="primary" />
            Action Plan
          </Typography>

          <Card
            sx={{
              border: t => `1px solid ${t.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              {/* Summary */}
              <Typography variant="body1" sx={{ mb: 2 }}>
                {actionPlan.summary}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip label={`${actionPlan.totalIssues} Total Issues`} size="small" variant="outlined" />
                <Chip label={`${actionPlan.criticalCount} Critical`} size="small" color="error" variant="outlined" />
              </Box>

              <Divider sx={{ mb: 1 }} />

              {/* Numbered Items */}
              {Array.isArray(actionPlanItems) &&
                actionPlanItems.map((item, idx) => (
                  <ActionPlanItem key={idx}>
                    <Box className="rank-badge">{item.rank ?? idx + 1}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.flag || item.title || `Item ${idx + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {item.explanation || item.description || ""}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {item.estimatedImpact && (
                          <Chip label={`Impact: ${item.estimatedImpact}`} size="small" color={impactColorMap[item.estimatedImpact?.toLowerCase()] || "default"} />
                        )}
                        {item.difficulty && (
                          <Chip label={`Difficulty: ${item.difficulty}`} size="small" color={difficultyColorMap[item.difficulty?.toLowerCase()] || "default"} variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </ActionPlanItem>
                ))}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Fix Recommendations Section */}
      {recommendations.length > 0 && (
        <Box>
          <Typography className="section-title">
            <AutoFixHighIcon color="primary" />
            Fix Recommendations
          </Typography>

          {recommendations.map(rec => (
            <RecommendationCard key={rec.id}>
              <CardContent>
                {/* Header Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {rec.flag}
                  </Typography>
                  <Chip label={rec.priority} size="small" color={priorityColorMap[rec.priority?.toLowerCase()] || "default"} />
                </Box>

                {/* URL */}
                {rec.url && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                    }}
                  >
                    {rec.url}
                  </Typography>
                )}

                {/* Explanation */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {rec.explanation}
                </Typography>

                {/* Fix Instructions */}
                {rec.fixInstructions && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                      Fix Instructions:
                    </Typography>
                    <pre className="fix-code">
                      <code>{rec.fixInstructions}</code>
                    </pre>
                  </Box>
                )}

                {/* Actions */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {rec.isApplied ? (
                    <Chip icon={<CheckCircleOutlineIcon />} label="Applied" color="success" size="small" />
                  ) : (
                    <Button variant="outlined" size="small" startIcon={<CheckCircleOutlineIcon />} onClick={() => handleMarkApplied(rec.id)} disabled={markLoading}>
                      Mark Applied
                    </Button>
                  )}
                </Box>
              </CardContent>
            </RecommendationCard>
          ))}
        </Box>
      )}
    </TabWrapper>
  );
};

export default AiRecommendationsTab;
